import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Puzzle, Check, Lock, CheckCircle2, Sparkles, ChevronRight } from "lucide-react";

/**
 * ModuliRegoleDialog — pannello Rule-Agnostic: preset + toggle per categoria.
 * Iter 55 (Feb 2026). Backend: /api/leagues/{id}/moduli
 *
 * I moduli 'locked' non possono essere disabilitati (auto-snapshots,
 * ruoli Mantra, import listone ufficiale).
 * I moduli 'planned' sono in arrivo — toggle disabilitato + badge.
 */
export default function ModuliRegoleDialog({ leagueId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState({});

  useEffect(() => {
    if (!open) return;
    setDirty({});
    setLoading(true);
    api.get(`/leagues/${leagueId}/moduli`)
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, leagueId]);

  const state = useMemo(() => ({ ...(data?.state || {}), ...dirty }), [data, dirty]);

  const catalogByCat = useMemo(() => {
    if (!data) return [];
    const groups = {};
    for (const m of data.catalog) {
      (groups[m.category] = groups[m.category] || []).push(m);
    }
    return (data.categories || []).map(c => ({ ...c, modules: groups[c.id] || [] }));
  }, [data]);

  const toggle = (mid, meta) => {
    if (meta.locked) {
      toast.info("Questo modulo è core e non può essere disabilitato.");
      return;
    }
    if (meta.stability === "planned") {
      toast.info("Questo modulo è in arrivo — non ancora funzionante.");
      return;
    }
    setDirty(prev => ({ ...prev, [mid]: !state[mid] }));
  };

  const applyPreset = async (preset) => {
    if (!window.confirm(`Applicare il preset "${preset.label}"? Questo sovrascrive TUTTI i toggle correnti.`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/leagues/${leagueId}/moduli/preset`, { preset_id: preset.id });
      toast.success(`✅ Preset "${preset.label}" applicato`);
      setData(prev => ({ ...prev, state: r.data.state }));
      setDirty({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const savePending = async () => {
    if (Object.keys(dirty).length === 0) {
      toast.info("Nessuna modifica da salvare.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/leagues/${leagueId}/moduli`, { modules: dirty });
      toast.success(`✅ ${Object.keys(r.data.applied || {}).length} moduli aggiornati`);
      setData(prev => ({ ...prev, state: r.data.state }));
      setDirty({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="moduli-dialog"
    >
      <div
        className="bg-slate-950 border border-slate-700 rounded-2xl max-w-4xl w-full my-8 relative"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-slate-800 bg-gradient-to-r from-cyan-950/40 to-slate-950 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
              <Puzzle size={20} className="text-cyan-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold">Rule-Agnostic Engine</div>
              <h2 className="text-xl font-black text-white">Moduli & Regole</h2>
            </div>
          </div>
          <button onClick={onClose} data-testid="moduli-close" className="text-slate-400 hover:text-white">
            <X size={22} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400">
            <Loader2 className="animate-spin inline mr-2" size={16} /> Caricamento moduli…
          </div>
        ) : !data ? null : (
          <div className="p-5 space-y-6">
            {/* Preset picker */}
            <div>
              <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                <Sparkles size={12} className="text-amber-400" /> Preset rapidi
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                {data.presets.map(p => (
                  <button
                    key={p.id}
                    onClick={() => applyPreset(p)}
                    disabled={saving}
                    data-testid={`preset-${p.id}`}
                    className="group text-left rounded-xl border border-slate-800 bg-slate-900/60 hover:border-cyan-500/60 hover:bg-slate-900 p-3 transition-all disabled:opacity-50"
                  >
                    <div className="text-sm font-black text-white flex items-center justify-between">
                      {p.label}
                      <ChevronRight size={14} className="text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{p.description}</div>
                    <div className="text-[10px] text-cyan-400/70 mt-1.5 font-mono">
                      {p.enabled_modules.length} moduli attivi
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle grid by category */}
            {catalogByCat.map(cat => (
              <div key={cat.id} data-testid={`category-${cat.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-1 h-6 rounded-full"
                    style={{ background: cat.color }}
                  />
                  <h3 className="text-sm font-black uppercase tracking-widest text-white">{cat.label}</h3>
                  <div className="flex-1 h-px bg-slate-800" />
                  <div className="text-[10px] text-slate-500 font-mono">
                    {cat.modules.filter(m => state[m.id]).length}/{cat.modules.length} attivi
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {cat.modules.map(m => {
                    const active = !!state[m.id];
                    const isDirty = dirty[m.id] !== undefined;
                    const isPlanned = m.stability === "planned";
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggle(m.id, m)}
                        disabled={saving || m.locked || isPlanned}
                        data-testid={`module-${m.id}`}
                        className={`text-left rounded-xl border p-3 transition-all ${
                          active
                            ? "border-emerald-500/50 bg-emerald-500/[0.07]"
                            : "border-slate-800 bg-slate-900/40 hover:border-slate-600"
                        } ${m.locked || isPlanned ? "cursor-not-allowed" : "cursor-pointer"} ${
                          isDirty ? "ring-2 ring-amber-500/40" : ""
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-sm font-black ${active ? "text-emerald-300" : "text-slate-300"}`}>
                                {m.label}
                              </span>
                              {m.locked && (
                                <span title="Modulo core, non disabilitabile">
                                  <Lock size={11} className="text-slate-500" />
                                </span>
                              )}
                              {isPlanned && (
                                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                  in arrivo
                                </span>
                              )}
                              {isDirty && (
                                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/30 text-amber-200">
                                  modificato
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{m.description}</div>
                          </div>
                          {/* Toggle switch */}
                          <div
                            className={`shrink-0 w-10 h-6 rounded-full relative transition-colors ${
                              active ? "bg-emerald-500" : "bg-slate-700"
                            } ${m.locked || isPlanned ? "opacity-50" : ""}`}
                          >
                            <div
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
                                active ? "left-[18px]" : "left-0.5"
                              }`}
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-between gap-3 p-4 border-t border-slate-800 bg-slate-950 rounded-b-2xl">
          <div className="text-xs text-slate-500">
            {Object.keys(dirty).length > 0 ? (
              <span className="text-amber-300 font-bold">
                {Object.keys(dirty).length} modifiche non salvate
              </span>
            ) : (
              <span>Modifiche salvate ✓</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest border border-slate-700 text-slate-300 hover:border-slate-500"
              data-testid="moduli-cancel"
            >
              Chiudi
            </button>
            <button
              onClick={savePending}
              disabled={saving || Object.keys(dirty).length === 0}
              data-testid="moduli-save"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-cyan-400 to-cyan-600 text-slate-900 hover:brightness-110 disabled:opacity-40 shadow-lg shadow-cyan-500/20"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Salva modifiche
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
